import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { boom } from './audio';
import { fireLights, hitPoint, removeMark } from './beam';
import { hideProps } from './city';
import { addDollars, chainHit } from './combo';
import { buildings, debris, fires } from './entities';
import { CHAR, facadeSet } from './facades';
import { spawnGem } from './gems';
import { toast } from './hud';
import { minimap } from './minimap';
import { dust, embers, smoke, sparks } from './particles';
import { physMat, world } from './physics';
import { rig, scene } from './scene';
import { S } from './state';
import { killCarsNear } from './traffic';
import { clamp, fmt$, rnd } from './util';

export const boxGeo = new THREE.BoxGeometry(1,1,1);
// rubble shapes: one clean slab plus jittered dodecahedra, so a heap reads as broken concrete rather than dice
const shardGeos: THREE.BufferGeometry[] = [boxGeo];
for (let v = 0; v < 4; v++){ const g = new THREE.DodecahedronGeometry(0.62, 0); const p = g.attributes.position as THREE.BufferAttribute; for (let i = 0; i < p.count; i++) p.setXYZ(i, p.getX(i) + rnd(-0.12, 0.12), p.getY(i) + rnd(-0.12, 0.12), p.getZ(i) + rnd(-0.12, 0.12)); g.computeVertexNormals(); shardGeos.push(g); }
const GREY = new THREE.Color('#6a6461');

// lowest street level around a footprint (photo world), 0 in Classic
function footGround(u: any){
  if (S.world !== 'photo') return 0;
  let gy: number | null = null;
  for (const [ox, oz] of [[-1,-1],[1,-1],[1,1],[-1,1]]){ const y = sampleGround(u.center.x + ox*(u.size.x/2+4), u.center.z + oz*(u.size.z/2+4)); if (y !== null && (gy === null || y < gy)) gy = y; }
  return gy ?? 0;
}

export function destroyBuilding(b, hitPoint, stomp, opts?: { remote?: boolean; byMe?: boolean; value?: number; mult?: number; byName?: string }){
  const u = b.userData; const remote = !!(opts && opts.remote);
  scene.remove(b); buildings.splice(buildings.indexOf(b),1); hideProps(b);
  if (u.marks) while (u.marks.length) removeMark(u.marks[0]);
  const gy = footGround(u);
  if (S.world==='photo'){
    // cut the footprint out of the photo mesh at street level; drop scorch decals that sat on it
    paintVoid(u.pts, gy); startCollapse(u.center.x, u.center.z, u.size.x/2, u.size.z/2, gy + u.h, gy);
    for (let i=marks.length-1;i>=0;i--){ const r = marks[i]; if (r.p && Math.hypot(r.p.x-u.center.x, r.p.z-u.center.z) < u.fr + 4) removeMark(r); }
  }
  if (!remote){ S.destroyed++; if (stomp) S.stomped++; chainHit(); addDollars(u.value, new THREE.Vector3(u.center.x, u.h*0.6, u.center.z), u.beacon || u.vol > 80000); }
  else if (opts.byMe){ S.destroyed++; if (stomp) S.stomped++; if (opts.value) popup(fmt$(opts.value) + (opts.mult && opts.mult > 1.01 ? ` ×${opts.mult.toFixed(1)}` : ''), new THREE.Vector3(u.center.x, u.h*0.6, u.center.z), u.vol > 80000); }
  else if (opts.byName) popup(opts.byName, new THREE.Vector3(u.center.x, u.h*0.6, u.center.z), false);
  killCarsNear(u.center.x, u.center.z, u.fr + 12, 30);
  if (!remote && !stomp && u.vol > 50000 && Math.random() < 0.35) setTimeout(() => { if (S.phase==='play') spawnGem(new THREE.Vector3(u.center.x, 0, u.center.z)); }, 1800);
  if (u.beacon){ const i = S.beacons.findIndex(x=>x.b===b); if (i>=0){ scene.remove(S.beacons[i].mesh); S.beacons.splice(i,1); } toast('Priority target down', fmt$(u.value * S.mult)); }
  const v = clamp(u.vol/120000, 0.08, 1);
  rig.shake += clamp(0.4 + u.vol/40000, 0.4, 4.5); boom(v);
  const {size, center} = u;
  const photo = S.world === 'photo';
  const tint: THREE.Color | null = u.tint || null;

  // ── rubble: pieces of a size that suits the building (6–15 m), never more than 48 bodies; the rest of the mass is dust
  const cs = clamp(Math.cbrt(u.vol) / 4.2, 6, 15);
  const nx = clamp(Math.round(size.x / cs), 1, 5), nz = clamp(Math.round(size.z / cs), 1, 5);
  const ny = clamp(Math.min(Math.round(u.h / cs), Math.floor(48 / (nx * nz))), 1, 6);
  const fs = facadeSet[(Math.random()*facadeSet.length)|0];
  const base = photo ? (tint || GREY).clone().lerp(CHAR, 0.35) : u.isGlass ? u.base.clone().lerp(CHAR, 0.55) : new THREE.Color('#ffffff').lerp(CHAR, 0.42);
  const chunkMat = new THREE.MeshStandardMaterial({color:base, map:photo ? getRubbleTex() : (u.isGlass ? null : fs.chunk), roughness:.95, metalness:(!photo && u.isGlass) ? .5 : 0, flatShading:true, emissive:new THREE.Color('#ff5a1e'), emissiveIntensity:0.5});
  chunkMat.userData.born = performance.now();
  const cw = size.x / nx, cd = size.z / nz;
  for (let ix=0; ix<nx; ix++) for (let iz=0; iz<nz; iz++) for (let iy=0; iy<ny; iy++){
    const pos = new THREE.Vector3(center.x-size.x/2+cw*(ix+.5) + rnd(-2, 2), gy + cs*(iy+.6) + rnd(0, cs*0.4), center.z-size.z/2+cd*(iz+.5) + rnd(-2, 2));
    const sx = cs*rnd(0.5, 1.1), sy = cs*rnd(0.35, 0.9), sz = cs*rnd(0.5, 1.1);
    const mesh = new THREE.Mesh(shardGeos[(Math.random()*shardGeos.length)|0], chunkMat);
    mesh.scale.set(sx, sy, sz); mesh.rotation.set(rnd(0, 6), rnd(0, 6), rnd(0, 6)); mesh.castShadow = true; scene.add(mesh);
    const body = new CANNON.Body({mass:sx*sy*sz*0.02, material:physMat, shape:new CANNON.Box(new CANNON.Vec3(sx*0.42, sy*0.42, sz*0.42))});
    body.position.set(pos.x,pos.y,pos.z); body.quaternion.set(mesh.quaternion.x, mesh.quaternion.y, mesh.quaternion.z, mesh.quaternion.w);
    const away = pos.clone().sub(hitPoint); away.y = 0; away.normalize();
    const kick = (stomp ? 14 : 6) + Math.random()*10;
    body.velocity.set(away.x*kick + (Math.random()-.5)*8, 4 + Math.random()*9 + iy*3, away.z*kick + (Math.random()-.5)*8);
    body.angularVelocity.set((Math.random()-.5)*4,(Math.random()-.5)*4,(Math.random()-.5)*4);
    body.sleepSpeedLimit = 0.8; world.addBody(body);
    debris.push({mesh, body, born:performance.now(), floor:gy, hy:sy*0.42});
  }
  // Classic: the empty shell sinks into its own footprint while the chunks fly
  if (!photo){ const shell = b.clone(); shell.material = b.material; scene.add(shell); sinking.push({mesh:shell, t:0, h:u.h}); }
  while (debris.length > 420){ const d = debris.shift(); scene.remove(d.mesh); world.removeBody(d.body); if (!debris.some(o => o.mesh.material === d.mesh.material)) d.mesh.material.dispose(); }
  // what's left: a smouldering heap over the footprint, and the dust wave rolling out of it
  addRuin(u, gy, tint);
  dustWave(center.x, center.z, gy, Math.max(6, u.fr*0.7), u.fr*2.4 + 14, 1.4 + clamp(u.h/120, 0, 1.2));
  const nDust = clamp(50 + u.vol/1200, 50, 260);
  for (let i=0;i<nDust;i++){ const p = new THREE.Vector3(center.x+(Math.random()-.5)*size.x, gy + Math.random()*u.h*.6, center.z+(Math.random()-.5)*size.z); dust.emit(p, new THREE.Vector3(0, 6+Math.random()*10, 0), 24, 3.8); }
  for (let i=0;i<50;i++) sparks.emit(hitPoint, new THREE.Vector3(0,20,0), 40, 0.8);
  if (!stomp && u.vol > 15000){
    while (fires.length >= fireLights.length){ const f = fires.shift(); f.light.intensity = 0; }
    const light = fireLights.find(l => !fires.some(f => f.light===l)); light.position.set(center.x, gy + 6, center.z);
    fires.push({light, pos:new THREE.Vector3(center.x, gy + 2, center.z), born:performance.now(), r:Math.max(size.x,size.z)/2, life:7+u.vol/60000});
  }
  minimap.dirty = true;
}
export const sinking = [];
export function updateSinking(dt){
  for (let i=sinking.length-1;i>=0;i--){ const s = sinking[i]; s.t += dt; const k = Math.min(1, s.t/0.55); const e = k*k;
    s.mesh.scale.y = Math.max(0.02, 1 - e*0.97); s.mesh.rotation.z = (Math.random()-.5)*0.01 + e*0.06; s.mesh.rotation.x = e*0.04;
    if (k >= 1){ scene.remove(s.mesh); sinking.splice(i,1); } }
}
export function updateDebris(dt, now){
  world.step(1/60, dt, 3); updateSinking(dt); updateDustWaves(dt); updateRuins(now);
  for (let i=debris.length-1;i>=0;i--){
    const d = debris[i]; const age = (now - d.born)/1000;
    if (age > 16){ scene.remove(d.mesh); world.removeBody(d.body); debris.splice(i,1); if (!debris.some(o => o.mesh.material === d.mesh.material)) d.mesh.material.dispose(); continue; }
    if (d.floor && d.body.position.y < d.floor + d.hy){ d.body.position.y = d.floor + d.hy; if (d.body.velocity.y < 0) d.body.velocity.y *= -0.25; }   // hilly photo cities: land on the real street level
    d.mesh.position.copy(d.body.position); d.mesh.quaternion.copy(d.body.quaternion);
    const m = d.mesh.material; if (m.userData.born !== undefined){ const a = (now - m.userData.born)/1000; m.emissiveIntensity = 0.5*Math.exp(-a*1.6); }
    if (age > 13) d.mesh.position.y -= (age-13)*(age-13)*8;   // the loose pieces settle into the heap
  }
  for (let i=fires.length-1;i>=0;i--){
    const f = fires[i]; const age = (now-f.born)/1000; const k = clamp(1 - age/f.life, 0, 1);
    f.light.intensity = 9000*k*(0.75+Math.random()*0.5);
    if (Math.random() < 0.6*k) smoke.emit(new THREE.Vector3(f.pos.x+(Math.random()-.5)*f.r, f.pos.y, f.pos.z+(Math.random()-.5)*f.r), new THREE.Vector3(0, 9+Math.random()*6, 0), 5, 6);
    if (Math.random() < 0.5*k) embers.emit(new THREE.Vector3(f.pos.x+(Math.random()-.5)*f.r, f.pos.y, f.pos.z+(Math.random()-.5)*f.r), new THREE.Vector3(0, 8, 0), 6, 2.5);
    if (k <= 0){ f.light.intensity = 0; fires.splice(i,1); }
  }
}

export function damage(b, amt, now){ const u = b.userData; u.hitT = now; u.dirty = true;
  if (mpHooks.damage){ mpHooks.damage(b, amt); u.hp = Math.max(0.04*u.maxHp, u.hp - amt); return; }   // server decides collapses in World mode
  u.hp -= amt; if (u.hp <= 0) destroyBuilding(b, hitPoint, false); }
// World mode hooks (set by multiplayer.ts while connected)
export const mpHooks: { damage: null | ((b: any, amt: number) => void); hammer: null | ((p: THREE.Vector3) => void); crush: null | ((b: any) => void) } = { damage: null, hammer: null, crush: null };
// remove a building that was destroyed before we arrived: no rubble flying, no score, just the cold heap
export function removeBuildingQuietly(b){
  const u = b.userData; const i = buildings.indexOf(b); if (i < 0) return;
  scene.remove(b); buildings.splice(i, 1); hideProps(b);
  if (u.marks) while (u.marks.length) removeMark(u.marks[0]);
  const gy = footGround(u);
  if (S.world==='photo') paintVoid(u.pts, gy);
  addRuin(u, gy, null, true);
  minimap.dirty = true;
}
import { sampleGround, paintVoid, startCollapse } from '../world/tiles';
import { marks } from './beam';
import { popup } from './combo';
import { addRuin, updateRuins, dustWave, updateDustWaves, getRubbleTex } from './ruins';
